import { QuestionRulesConditions } from '@app/src/components/Drawer/Views/DrawerViewAddQuestionRule'
import DirectFileUploadField from '@app/src/components/Form/DirectFileUploadField'
import ResponseItemContextProvider from '@app/src/context/ResponseItemContextProvider'
import useQuestionRules from '@app/src/hooks/useQuestionRules'
import type { RequestItem } from '@app/src/types/resourceExplorer'
import { AnswerStatus } from '@app/src/types/resourceExplorer'
import { Stack } from '@mui/material'
import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import CoordinateListInput from './CoordinateListInput'
import { RESPONSE_CONTENT_BOTTOM_PADDING } from './SuggestedAnswers/QuestionSuggestionFooter'
import OptionSelectWithSuggestion from './OptionSelectWithSuggestion'
import TextFieldWithSuggestions from './TextFieldWithSuggestions'

export enum PreviewMode {
  Regular = 'regular',
  SelectionDisabled = 'selectionDisabled',
}
export interface EditResponseProps {
  questionType: string
  requestItem: RequestItem
  periodName?: string
  responseItemId?: number
  previewMode?: PreviewMode
  sectionNumber: number
  itemNumber: number
}

const EditResponse = ({
  questionType,
  requestItem,
  responseItemId,
  previewMode,
  sectionNumber,
  itemNumber,
}: EditResponseProps): JSX.Element => {
  const { errors, watch, register, setValue } = useFormContext()
  const { applySkipRuleEffectsToQuestion } = useQuestionRules(requestItem)

  const requestItemId = requestItem.id
  const answer = `${requestItemId}.answer`
  const selectedCollection = `${requestItemId}.selectedOptions`
  const fileUpload = `${requestItemId}.uploadReferences`
  const cannotAnswer = `${requestItemId}.cannotAnswer`
  const answerStatus = `${requestItemId}.answerStatus`

  const hasError = Boolean(errors?.[`${requestItemId}`])
  const error = hasError ? errors[`${requestItemId}`].message : null

  const cannotAnswerWatch = watch(cannotAnswer)
  const answerStatusWatch = watch(answerStatus)
  const fileUploadWatch = watch(`${fileUpload}.originalFileName`)

  useEffect(() => {
    if (cannotAnswerWatch && answerStatusWatch !== AnswerStatus.Excluded) {
      setValue(answer, undefined)
      setValue(fileUpload, undefined)
      setValue(selectedCollection, undefined)
      setValue(`${requestItemId}.coordinates`, undefined)
    }
  }, [cannotAnswerWatch, answerStatusWatch])

  useEffect(() => {
    if (questionType === 'file' && fileUploadWatch && requestItem.questionId) {
      applySkipRuleEffectsToQuestion(requestItem.questionId, [QuestionRulesConditions.FileUploaded])
    }
  }, [fileUploadWatch, questionType, applySkipRuleEffectsToQuestion, requestItem.questionId])

  return (
    <ResponseItemContextProvider
      cannotAnswer={cannotAnswerWatch}
      requestItem={requestItem}
      sectionNumber={sectionNumber}
      itemNumber={itemNumber}
    >
      <Stack pb={previewMode === PreviewMode.SelectionDisabled ? 0 : RESPONSE_CONTENT_BOTTOM_PADDING} spacing={1}>
        <input type="hidden" name={`${requestItemId}.id`} ref={register()} />
        {questionType === 'options' && (
          <OptionSelectWithSuggestion
            requestItem={requestItem}
            disabled={cannotAnswerWatch}
            error={error}
            hasError={hasError}
            previewMode={previewMode}
          />
        )}
        {(questionType === 'text' || questionType === 'number') && (
          <TextFieldWithSuggestions
            requestItem={requestItem}
            disabled={cannotAnswerWatch || previewMode === PreviewMode.SelectionDisabled}
            hasError={hasError}
            error={error}
            isPreview={Boolean(previewMode)}
          />
        )}
        {questionType === 'coordinates' && (
          <CoordinateListInput
            requestItemId={requestItemId}
            allowMultiple={requestItem.allowMultiChoice ?? false}
            disabled={cannotAnswerWatch || previewMode === PreviewMode.SelectionDisabled}
          />
        )}
        {questionType === 'file' && (responseItemId || Boolean(previewMode)) && (
          <DirectFileUploadField
            required
            requestItem={requestItem}
            name={fileUpload}
            id={requestItemId}
            validationError={error}
            disabled={cannotAnswerWatch || previewMode === PreviewMode.SelectionDisabled}
            sourceObjectId={responseItemId ?? 0}
            sourceObjectType="ResponseItem"
            isPreview={Boolean(previewMode)}
          />
        )}
      </Stack>
    </ResponseItemContextProvider>
  )
}

export default EditResponse
